import data from "../../shared/sharedData";

export default {
  name: "Employer Information",
  pageInfo: {
    text: `<p><b>IMPORTANT:</b> The information entered in this section must be the same as the employer information issued by the Department for the employer’s job opportunity on the PWD Form ETA-9141.`,
    coloring: "grey",
  },
  sections: [
    {
      name: "Employer Name(s)",
      fields: [
        {
          machine_name: "empBusinessName",
          widget: "text",
          code: "C.1",
          label: "Legal Business Name",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full Employer Name.",
            },
          ],
          maxLength: 120,
          tooltip: `Enter the full name of the individual employer, joint employer, job contractor, partnership, corporation, 
            i.e. the employer filing this application.  The employer’s full legal business name is the exact name of the 
            individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service (IRS).`,
        },
        {
          machine_name: "empTradeName",
          code: "C.2",
          widget: "text",
          label: "Trade name/Doing Business As (DBA), if applicable",
          maxLength: 120,
          tooltip:
            "Enter the full trade name or “Doing Business As” (DBA) name, if applicable, of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application.",
        },
      ],
    },
    {
      name: "Employer Address",
      fields: [
        {
          machine_name: "empAddr1",
          widget: "text",
          code: "C.3",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Address 1 must contain at least 2 characters.",
            },
            "noNAs",
          ],
          maxLength: 60,
          tooltip:
            "Enter the street address of the employer’s principal place of business.  The place of business must be a physical location and not a Post Office (P.O.) Box.",
        },
        {
          machine_name: "empAddr2",
          code: "C.4",
          widget: "text",
          label: `Address 2 <i>(apartment/suite/floor and number)</i>`,
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this field to complete the employer’s street address.  If no additional space is needed, enter “N/A.“",
        },
        {
          code: "C.8",
          label: "Country",
          machine_name: "empCountry",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["empState", "empProvince"],
          dynamicUpdates: [
            {
              if: "UNITED STATES OF AMERICA",
              operator: "!=",
              set: "empPostcode",
              to: null,
            },
          ],
          tooltip:
            "Enter the country of the employer’s principal place of business.  ",
        },
        {
          machine_name: "empCity",
          code: "C.5",
          widget: "text",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          label: `City`,
          maxLength: 35,
          tooltip:
            "Enter the city of the employer’s principal place of business.",
        },
        {
          machine_name: "empState",
          code: "C.6",
          widget: "select",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "empCountry",
          isVisible: {
            when: "empCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `State`,
          maxLength: 35,
          tooltip:
            "Enter the State, District, or Territory of the employer’s principal place of business.",
        },
        {
          machine_name: "empPostcode",
          code: "C.7",
          widget: "text",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            {
              postalCountry: "empCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "empCountry",
              },
            },
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `Postal code`,
          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer’s principal place of business.",
        },
        {
          code: "C.9",
          label: "Province",
          machine_name: "empProvince",
          widget: "text",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip:
            "Enter the province of the employer’s principal place of business, if applicable.  Enter “N/A” if not applicable.",
          maxLength: 25,
        },
      ],
    },
    {
      name: "Employer Contact Information",
      fields: [
        {
          code: "C.10",
          machine_name: "empPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and telephone number for the employer’s principal place of business. Include country code, if outside of the United States.",
        },
        {
          code: "C.11",
          machine_name: "empPhoneext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number for the employer’s principal place of business, if applicable.  Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer Identifiers",
      fields: [
        {
          code: "C.12",
          machine_name: "empFein",
          widget: "text",
          label: "Federal Employer Identification Number (FEIN from IRS)",
          disabled: true,
          disableWhenField: (formValues) => !!formValues.registrationNumber,
          validate: [
            "required",
            "fein",
            { minLength: 10, message: "This is not a valid FEIN" },
            {
              FEINPrefixValidator: "empFein",
              needsFieldValues: true,
              message: "This is not a valid FEIN",
            },
          ],
          clearSchema: ["registrationNumber", "isRegistrationNumberLinked"],
          parse: "fein",
          maxLength: 10,
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) as assigned by the IRS.  
            Do not enter a social security number. <br></br> <b>Note</b>: All employers, including private households, 
            MUST obtain an FEIN from the IRS before completing this application.  Information on obtaining an FEIN 
            can be found at <a target='_blank' href='http://www.IRS.gov'>www.IRS.gov</a>.`,
        },
        {
          code: "C.13",
          machine_name: "empNaics",
          field_to_modify: "empNaicsDescription",
          widget: "naics",
          label: "NAICS Code",
          validate: ["required"],
          tooltip: `Enter the four-to-six digit North American Industry Classification System (NAICS) code that best describes the 
            employer’s business, not the H-2B job opportunity.  A listing of NAICS codes can be found at 
            <a href='https://www.census.gov/naics/' rel='noopener noreferrer' target='_blank'>https://www.census.gov/naics/</a>.`,
        },
      ],
    },
  ],
};
